import { useQuery } from "@tanstack/react-query"
import { Inbox } from "lucide-react"

import { api } from "@/api/cliente"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha } from "@/lib/fechas"

/**
 * Lo que uno mismo ha reportado.
 *
 * ⚠ **Existe porque sin esto el canal es un buzón sin acuse.** Se manda una falla,
 * sale «Reporte #77 enviado», y se acabó: no hay forma de volver a mirarlo, de saber
 * si alguien lo vio, ni de comprobar qué se decidió. La segunda vez que pasa, la
 * gente deja de reportar y lo dice de palabra — que es justo lo que este canal venía
 * a sustituir.
 *
 * ⚠ **Se ve con `reporte:crear`, no con `reporte:triar`.** Lo de otros lleva su
 * traza y no es material de consulta general; lo propio sí, y negarlo sería negarle
 * a alguien lo que él mismo escribió.
 *
 * ⚠ **Se enseña el ESTADO y la resolución, no el veredicto automático.** A quien
 * reportó le importa si se está atendiendo y qué se decidió; el veredicto del
 * clasificador es una herramienta de reparto, y enseñarlo aquí invitaría a discutir
 * con una etiqueta en vez de con quien lo triió.
 */

type Resumen = {
  id: number
  titulo: string
  vista: string
  estado: "NUEVO" | "ACEPTADO" | "DESCARTADO"
  creado_en: string
  tarea_id: number | null
}

const TONO = { NUEVO: "primario", ACEPTADO: "exito", DESCARTADO: "neutro" } as const

const QUE_SIGNIFICA = {
  NUEVO: "Enviado, pendiente de revisión",
  ACEPTADO: "Aceptado: hay trabajo que hacer",
  DESCARTADO: "Revisado y descartado",
}

export function MisReportes() {
  const reportes = useQuery({
    queryKey: ["reportes", "mios"],
    queryFn: () => api.get<Resumen[]>("/api/v1/reportes/mios"),
  })

  if (reportes.isPending) {
    return (
      <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (reportes.isError) {
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudieron cargar tus reportes.
      </p>
    )
  }

  if (reportes.data.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-2 superficie py-8"
        style={{ color: "var(--fg-muted)" }}
      >
        <Inbox size={20} aria-hidden={true} />
        <p className="text-sm">Todavía no has reportado nada.</p>
      </div>
    )
  }

  return (
    <ul className="space-y-2">
      {reportes.data.map((r) => (
        <li key={r.id} data-testid={`mio-${r.id}`} className="superficie p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Insignia tono={TONO[r.estado]}>{QUE_SIGNIFICA[r.estado]}</Insignia>
            <span className="flex-1 text-sm font-medium">{r.titulo}</span>
            <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
              {r.vista} · {comoFecha(r.creado_en)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  )
}
